import { DropdownDynamic } from 'legacy/lut/library/dropdown'
import { useState } from 'react'

import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { Input } from 'core-components/library/inputs'
import { Controller, ValidateInput, ValidationStatus } from 'core-components/library/validation'
import { Crud } from 'core/types/Crud'

import { IJbpPartners, TSimpleTableFilterKeys } from '../models'

import { checkPartnerName } from './helpers/checkPartnerName'
import { useFilterArea } from './hooks/useFilterArea'
import { useFilterAreaCrud } from './hooks/useFilterAreaCrud'

import styles from './FilterArea.module.scss'

interface IProps {
  crudSource: Crud<IJbpPartners, string | number>
  usedFilters?: TSimpleTableFilterKeys[]
}

export const FilterArea = ({
  crudSource,
  usedFilters = ['partnerId', 'partnerName', 'market', 'country'] as TSimpleTableFilterKeys[],
}: IProps) => {
  const {
    onFilter,
    filterApplied,
    onResetFilter,
    market,
    setMarket,
    country,
    setCountry,
    partnerId,
    setPartnerId,
    partnerName,
    setPartnerName,
  } = useFilterArea(crudSource)
  const { marketsCrud, countriesCrud } = useFilterAreaCrud(market)

  const [validationStatus, setValidationStatus] = useState(ValidationStatus.Valid)

  return (
    <div className={styles.root}>
      <Controller
        onValidationStatusChange={({ status }) => {
          if (status !== ValidationStatus.WIP) {
            setValidationStatus(status)
          }
        }}
      >
        <div className={styles.row}>
          <RenderWhen condition={usedFilters.includes('partnerId')}>
            <Input
              classes={{
                root: styles.input,
              }}
              label="Partner Id"
              onChange={setPartnerId}
              value={partnerId}
            />

            <Button
              className={styles.iconButton}
              disabled={!partnerId}
              iconButton="kbase kbase-close kbase-sm"
              color="danger"
              onClick={() => {
                setPartnerId('')
              }}
            />
          </RenderWhen>

          <RenderWhen condition={usedFilters.includes('partnerName')}>
            <ValidateInput
              classes={{
                validatorClasses: {
                  root: styles.input,
                },
              }}
              label="Partner Name"
              onChange={setPartnerName}
              value={partnerName}
              checkFn={checkPartnerName}
            />
            <Button
              className={styles.iconButton}
              disabled={!partnerName}
              iconButton="kbase kbase-close kbase-sm"
              color="danger"
              onClick={() => {
                setPartnerName('')
              }}
            />
          </RenderWhen>

          <RenderWhen condition={usedFilters.includes('market')}>
            <DropdownDynamic
              label="Market"
              translation="jbp-reports-market"
              source={marketsCrud}
              value={market}
              caption="Market"
              onChange={setMarket}
            />
            <Button
              className={styles.iconButton}
              disabled={!market}
              iconButton="kbase kbase-close kbase-sm"
              color="danger"
              onClick={() => {
                setMarket('')
              }}
            />
          </RenderWhen>
          <RenderWhen condition={usedFilters.includes('country')}>
            <DropdownDynamic
              disabled={!market}
              source={countriesCrud}
              caption="name"
              label="Country"
              translation="jbp-reports-country"
              onChange={setCountry}
              value={country}
            />
            <Button
              className={styles.iconButton}
              disabled={!country}
              iconButton="kbase kbase-close kbase-sm"
              color="danger"
              onClick={() => {
                setCountry('')
              }}
            />
          </RenderWhen>
        </div>
      </Controller>

      <div className={styles.row}>
        <Button
          disabled={validationStatus !== ValidationStatus.Valid}
          className={styles.filterButton}
          onClick={onFilter}
        >
          <Translation text="jbp-partner-scope-management-filter" />
        </Button>
        <Button disabled={!filterApplied} onClick={onResetFilter} color="danger">
          <Translation text="jbp-partner-scope-management-reset-filter" />
        </Button>
      </div>
    </div>
  )
}
